import { Icon, Tooltip } from '@zoom/components';
import { differenceInHours } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../../app/AuthorizationProvider';
import { TimezoneDSTChangeWithinRange } from '../../../../components/MySchedule/utils';
import { getTimeZone } from '../../../../utils/date';
import { MIN_15, ONE_HOUR } from '../../config';

interface CustomXAxisProps {
  end: number;
  highlightDSTChange?: TimezoneDSTChangeWithinRange;
  start: number;
  timezone?: string;
  width: number;
  testIdPrefix?: string;
}

const GanttXAxisMinutes: React.FC<{ hour: number; timezone: string }> = ({ hour, timezone }) => {
  const { localeObj } = useAuth();
  const { t } = useTranslation();

  const quarterHourTicks = [...Array(4)].map((_, index) => hour + index * MIN_15);

  return (
    <div className="gantt-x-axis__minute-wrapper">
      {quarterHourTicks.map((quarterHourTick) => (
        <div className="gantt-x-axis__minute" key={quarterHourTick}>
          {format(utcToZonedTime(quarterHourTick, timezone), ':mm', { locale: localeObj, timeZone: timezone })}
          <span className="schedule-time">
            {format(utcToZonedTime(quarterHourTick, timezone), t('date.formats.timeShort'), {
              locale: localeObj,
              timeZone: timezone,
            })}
          </span>
        </div>
      ))}
    </div>
  );
};

export const CustomXAxis: React.FC<CustomXAxisProps> = ({
  end,
  highlightDSTChange,
  start,
  width,
  timezone = getTimeZone(),
  testIdPrefix,
}) => {
  const { localeObj } = useAuth();
  const { t } = useTranslation();

  const hours = differenceInHours(end, start);
  const hoursTicks = [...Array(hours)].map((_, index: number) => start + index * ONE_HOUR);

  return (
    <div
      className="gantt-x-axis__container"
      style={{ width }}
      data-test-id={testIdPrefix && `${testIdPrefix}:custom-x-axis`}
    >
      {hoursTicks.map((hourTick) => (
        <div className="gantt-x-axis__hour-wrapper" key={hourTick}>
          <div className="gantt-x-axis__hour">
            {format(utcToZonedTime(hourTick, timezone), t('date.formats.timeShort'), {
              locale: localeObj,
              timeZone: timezone,
            })}
            {highlightDSTChange?.time === hourTick && (
              <Tooltip
                message={
                  <div data-test-id="tooltip-message">
                    <strong>
                      {format(utcToZonedTime(highlightDSTChange.date, timezone), t('date.formats.default'), {
                        locale: localeObj,
                        timeZone: timezone,
                      })}
                    </strong>{' '}
                    (
                    {format(utcToZonedTime(highlightDSTChange.date, timezone), 'EEEE', {
                      locale: localeObj,
                      timeZone: timezone,
                    })}
                    ) - <i>{t('time.dst')}</i>
                  </div>
                }
                placement="bottom"
              >
                <Icon
                  kind="time_change"
                  className="gantt-x-axis__dst-icon"
                  size={16}
                  color="red"
                  data-test-id="DST-change"
                />
              </Tooltip>
            )}
          </div>
          <GanttXAxisMinutes hour={hourTick} timezone={timezone} />
        </div>
      ))}
    </div>
  );
};
